import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, User, FileText } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Stepper,
  MultiSelect,
  type MultiSelectOption,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useCreateSurveyRecordsBulk,
  useSurveyedCustomerIds,
  type CreateSurveyRecordPayload,
  type SurveyRecordScores,
  type UpsalePotential,
} from '@/api/surveys';
import { CustomerSearchSelect } from '@/pages/contracts/new/customer-search-select';

/* ── Constants ───────────────────────────────────────────────────────────── */

const SCORE_MIN = 1;
const SCORE_MAX = 10;

const SCORE_FIELDS: { key: keyof SurveyRecordScores; label: string }[] = [
  { key: 'score_service_quality', label: 'Service Quality (1–10)' },
  { key: 'score_customer_care', label: 'Customer Care (1–10)' },
  { key: 'score_tech_support', label: 'Tech Support (1–10)' },
  { key: 'score_issue_resolution', label: 'Issue Resolution (1–10)' },
  { key: 'score_referral_willingness', label: 'Referral Willingness (1–10)' },
];

const NOTE_FIELDS: { key: keyof SurveyRecordScores; label: string }[] = [
  { key: 'note_service_quality', label: 'Service Quality note' },
  { key: 'note_customer_care', label: 'Customer Care note' },
  { key: 'note_tech_support', label: 'Tech Support note' },
  { key: 'note_issue_resolution', label: 'Issue Resolution note' },
  { key: 'note_referral_willingness', label: 'Referral Willingness note' },
  { key: 'note_general', label: 'General note' },
  { key: 'improvement', label: 'Improvement suggestions' },
];

const UPSALE_OPTIONS: MultiSelectOption[] = [
  { value: 'CLOUD', label: 'Cloud' },
  { value: 'CDN', label: 'CDN' },
  { value: 'VNIS', label: 'VNIS' },
];

const WIZARD_STEPS = [
  { label: 'Select customer', icon: <User size={16} /> },
  { label: 'Survey responses', icon: <FileText size={16} /> },
];

/* ── Score picker ────────────────────────────────────────────────────────── */

function ScorePicker({
  value,
  onChange,
  label,
  error,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
  label: string;
  error?: string;
}) {
  return (
    <Field label={label} error={error}>
      {() => (
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--vn-radius-sm)',
                border: `1px solid ${value === n ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                background: value === n ? 'var(--vn-primary-subtle)' : 'transparent',
                color: value === n ? 'var(--vn-primary-text)' : 'var(--vn-fg-default)',
                fontWeight: value === n ? 'var(--vn-font-weight-semibold)' : 'var(--vn-font-weight-regular)',
                fontSize: 'var(--vn-font-size-sm)',
                cursor: 'pointer',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </Field>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */

type ScoreErrors = Partial<Record<keyof SurveyRecordScores, string>>;

/**
 * `/surveys/records/new` — 2-step wizard: pick a customer, then fill in the
 * 5 satisfaction scores (1-10), optional notes/improvement, and upsale tags.
 * Submission calls `POST /surveys/bulk`. The USER confirms submission.
 */
export default function SurveyRecordNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateSurveyRecordsBulk();

  const [step, setStep] = useState(0);
  const [customerId, setCustomerId] = useState(0);
  const [scores, setScores] = useState<SurveyRecordScores>({});
  const [scoreErrors, setScoreErrors] = useState<ScoreErrors>({});
  const [submitError, setSubmitError] = useState('');

  // Load customer IDs already surveyed this quarter (for informational context).
  const { data: surveyedIds } = useSurveyedCustomerIds();
  const alreadySurveyed = useMemo(
    () => customerId > 0 && (surveyedIds ?? []).includes(customerId),
    [customerId, surveyedIds],
  );

  const setScore = (key: keyof SurveyRecordScores, value: number | string | string[]) => {
    setScores((prev) => ({ ...prev, [key]: value }));
    if (scoreErrors[key]) {
      setScoreErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validateScores = (): boolean => {
    const errs: ScoreErrors = {};
    for (const { key, label } of SCORE_FIELDS) {
      const v = scores[key] as number | undefined;
      if (!v || v < SCORE_MIN || v > SCORE_MAX) {
        errs[key] = `${label} is required (1–10)`;
      }
    }
    setScoreErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (step === 0) {
      if (!customerId) return;
      setStep(1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!validateScores()) return;
    setSubmitError('');

    const payload: CreateSurveyRecordPayload = {
      customer_id: customerId,
      records: {
        score_service_quality: scores.score_service_quality as number,
        score_customer_care: scores.score_customer_care as number,
        score_tech_support: scores.score_tech_support as number,
        score_issue_resolution: scores.score_issue_resolution as number,
        score_referral_willingness: scores.score_referral_willingness as number,
        note_general: scores.note_general || undefined,
        note_service_quality: scores.note_service_quality || undefined,
        note_customer_care: scores.note_customer_care || undefined,
        note_tech_support: scores.note_tech_support || undefined,
        note_issue_resolution: scores.note_issue_resolution || undefined,
        note_referral_willingness: scores.note_referral_willingness || undefined,
        improvement: scores.improvement || undefined,
        upsale_potential: (scores.upsale_potential as UpsalePotential[]) || [],
      },
    };

    try {
      await create.mutateAsync({ records: [payload] });
      toast.success('Survey record created');
      navigate('/surveys');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to create survey record.';
      setSubmitError(msg);
      toast.error('Failed to create survey record', { description: msg });
    }
  };

  return (
    <Container size="lg">
      <PageHeader
        title="New survey record"
        description="Record a customer satisfaction survey (scores 1–10 per category)."
        actions={
          <Button
            variant="ghost"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/surveys')}
          >
            Back to surveys
          </Button>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Stepper
          steps={WIZARD_STEPS}
          current={step}
          reached={step}
          onStepClick={(i) => i < step && setStep(i)}
          aria-label="Survey record wizard progress"
        />

        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            {step === 0 ? (
              /* ── Step 1: Customer selection ─────────────────────────── */
              <div style={{ maxWidth: 480 }}>
                <CustomerSearchSelect
                  value={customerId}
                  onChange={setCustomerId}
                  label="Customer"
                  required
                />
                {alreadySurveyed ? (
                  <Alert
                    tone="warning"
                    title="Already surveyed this quarter"
                    style={{ marginTop: 'var(--vn-space-3)' }}
                  >
                    This customer already has a survey record for the current quarter. Submitting
                    will update the existing record.
                  </Alert>
                ) : null}
              </div>
            ) : (
              /* ── Step 2: Scores + notes ──────────────────────────────── */
              <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                  {SCORE_FIELDS.map(({ key, label }) => (
                    <ScorePicker
                      key={key}
                      label={label}
                      value={scores[key] as number | undefined}
                      onChange={(v) => setScore(key, v)}
                      error={scoreErrors[key]}
                    />
                  ))}
                </div>

                <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                  {NOTE_FIELDS.map(({ key, label }) => (
                    <Field key={key} label={label}>
                      {({ id }) => (
                        <textarea
                          id={id}
                          value={(scores[key] as string) || ''}
                          onChange={(e) => setScore(key, e.target.value)}
                          rows={2}
                          style={{
                            width: '100%',
                            padding: 'var(--vn-space-2) var(--vn-space-3)',
                            border: '1px solid var(--vn-border-default)',
                            borderRadius: 'var(--vn-radius-sm)',
                            fontSize: 'var(--vn-font-size-sm)',
                            color: 'var(--vn-fg-default)',
                            background: 'var(--vn-bg-surface)',
                            resize: 'vertical',
                            fontFamily: 'var(--vn-font-sans)',
                          }}
                        />
                      )}
                    </Field>
                  ))}
                </div>

                <Field label="Upsale potential">
                  {() => (
                    <MultiSelect
                      options={UPSALE_OPTIONS}
                      value={(scores.upsale_potential as string[]) ?? []}
                      onChange={(v) => setScore('upsale_potential', v)}
                      placeholder="Select products with upsale potential…"
                    />
                  )}
                </Field>

                {submitError ? <Alert tone="danger" title={submitError} /> : null}
              </div>
            )}
          </CardBody>

          <CardFooter
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--vn-space-2)',
            }}
          >
            <Button
              variant="ghost"
              startIcon={<ArrowLeft size={15} />}
              onClick={() => (step === 0 ? navigate('/surveys') : setStep(0))}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>

            <Button
              onClick={handleNext}
              disabled={
                (step === 0 && !customerId) ||
                (step === 1 && create.isPending)
              }
              loading={step === 1 && create.isPending}
              startIcon={step === 1 ? <Check size={15} /> : undefined}
            >
              {step === 0 ? 'Next' : 'Submit'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </Container>
  );
}
